import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getCitiesByCountry } from "../services/cities";
import { getCitiesByCountry as getStaticCities } from "../constants/citiesByCountry";

const USE_API = true;

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

export const useCitiesByCountry = (country) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ["cities", { country }],
    getCitiesByCountry,
    {
      enabled: !!country && USE_API,
      staleTime: TWENTY_FOUR_HOURS,
      cacheTime: TWENTY_FOUR_HOURS * 7,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error("Failed to fetch cities:", error);
      },
    }
  );

  if (!USE_API) {
    return {
      data: getStaticCities(country),
      isLoading: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve({ data: getStaticCities(country) }),
      isFetching: false,
    };
  }

  const rawCitiesData = data?.data?.data || [];
  
  // Pre-process and optimize data structure
  const citiesData = useMemo(() => {
    if (!rawCitiesData || rawCitiesData.length === 0) {
      return [];
    }

    // Pre-process coordinates and create flat structure for better performance
    return rawCitiesData.map(state => ({
      ...state,
      // Pre-parse coordinates to avoid repeated splitting
      coordinates: state.coord ? {
        lat: parseFloat(state.coord.split(',')[0]),
        lng: parseFloat(state.coord.split(',')[1])
      } : null,
      // Pre-process children cities
      children: state.children?.map(city => ({
        ...city,
        coordinates: city.coord ? {
          lat: parseFloat(city.coord.split(',')[0]),
          lng: parseFloat(city.coord.split(',')[1])
        } : null,
      })) || []
    }));
  }, [rawCitiesData]);
  
  if (!USE_API || (isError && !data)) {
    console.log("Falling back to static data due to API error");
    const fallbackData = getStaticCities(country);
    
    // Apply same preprocessing to fallback data
    const processedFallback = fallbackData.map(state => ({
      ...state,
      coordinates: state.coord ? {
        lat: parseFloat(state.coord.split(',')[0]),
        lng: parseFloat(state.coord.split(',')[1])
      } : null,
      children: state.children?.map(city => ({
        ...city,
        coordinates: city.coord ? {
          lat: parseFloat(city.coord.split(',')[0]),
          lng: parseFloat(city.coord.split(',')[1])
        } : null,
      })) || []
    }));
    
    return {
      data: processedFallback,
      isLoading: false,
      isError: true,
      error,
      refetch,
      isFetching,
      isUsingFallback: true,
    };
  }

  return {
    data: citiesData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isUsingFallback: false,
  };
};

export default useCitiesByCountry;